/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef, useState } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Anchor, Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { useKeyboardReorderContext } from '../../../controllers/keyboard-reorder-provider';

import { useAppSelector } from './app-selector';
import type { AppTileProps } from './types';
import { UnsafeTile } from './unsafe-tile';

const styles = cssMap({
	appTile: {
		'&:hover': {
			textDecoration: 'none',
		},
	},
	anchor: {
		cursor: 'grab',
		'&:active': {
			cursor: 'grabbing',
		},
		textDecoration: 'none',
		width: '100%',
		'&:hover': {
			textDecoration: 'none',
		},
		'&:focus-visible': {
			outline: 'none',
		},
	},
	anchorDragging: {
		cursor: 'not-allowed',
		pointerEvents: 'none',
		textDecoration: 'none',
		width: '100%',
		'&:hover': {
			textDecoration: 'none',
		},
		'&:focus-visible': {
			outline: 'none',
		},
	},
	labelContainer: {
		borderRadius: token('radius.medium'),
		paddingLeft: token('space.025'),
		paddingRight: token('space.025'),
	},
});

export const AppTile = (props: AppTileProps): JSX.Element | null => {
	const { href, selectedApp, productId, isDragging, hideNotch, isDragPreview, isHovered, testId } =
		props;
	const [isFocusVisible, setIsFocusVisible] = useState(false);
	const hadPointerDownRef = useRef(false);
	const anchorRef = useRef<HTMLAnchorElement>(null);

	const {
		isKeyboardReorderModeEnabled,
		setKeyboardSelectedProductId,
		setIsKeyboardReorderToggleVisible,
		moveKeyboardReorderSelectedTile,
		keyboardReorderAppTileRefsMap,
		selectedKeyboardReorderProductId,
		cancelKeyboardReorder,
		applyKeyboardReorder,
		exitKeyboardReorderMode,
		containerRef,
	} = useKeyboardReorderContext();

	const isSelected = selectedApp === productId;
	const product = useAppSelector(productId);

	useEffect(() => {
		// Hide the keyboard reorder toggle when the tile is being dragged
		if (isDragging) {
			exitKeyboardReorderMode();
		}
	}, [exitKeyboardReorderMode, isDragging]);

	useEffect(() => {
		if (keyboardReorderAppTileRefsMap.current && anchorRef.current) {
			keyboardReorderAppTileRefsMap.current.set(productId, anchorRef.current);
		}
	}, [productId, keyboardReorderAppTileRefsMap]);

	if (!product) {
		return null;
	}

	const { icon, text } = product;
	const textComponent = (
		<Text maxLines={1} size="small" align="center" color="color.text.subtle">
			{text}
		</Text>
	);

	return (
		<Anchor
			href={href}
			testId={testId}
			ref={anchorRef}
			// Links are natively draggable in browsers, which can interfere with our tile-level
			// Pragmatic DnD interactions. Disable native link dragging so dragging can start
			// from anywhere on the tile.
			draggable={false}
			xcss={isDragging ? styles.anchorDragging : styles.anchor}
			aria-disabled={isDragging}
			tabIndex={isDragging ? -1 : undefined} // removes the tile from tab order when it is in the dragging state
			onDragStart={(e) => {
				e.preventDefault();
			}}
			onClick={(e) => {
				if (!isDragging) {
					return;
				}
				e.preventDefault();
				e.stopPropagation();
			}}
			onKeyDown={(e) => {
				if (!isDragging && !isKeyboardReorderModeEnabled) {
					return;
				}

				// prevent keyboard "click" while disabled
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					e.stopPropagation();
				}

				if (isKeyboardReorderModeEnabled) {
					if (e.key === ' ') {
						if (selectedKeyboardReorderProductId) {
							applyKeyboardReorder();
						} else {
							setKeyboardSelectedProductId(productId);
						}
					}

					if (e.key === 'ArrowUp') {
						moveKeyboardReorderSelectedTile('up');
					}

					if (e.key === 'ArrowDown') {
						moveKeyboardReorderSelectedTile('down');
					}

					if (e.key === 'Escape') {
						cancelKeyboardReorder();
					}
				}
			}}
			onPointerDown={() => {
				hadPointerDownRef.current = true;
				setIsFocusVisible(false);
			}}
			onFocus={() => {
				/**
				 * We intentionally avoid relying on `:focus-visible` here, since programmatic focus
				 * (used by VR tests) may not always count as focus-visible depending on the browser.
				 * This heuristic shows the focus ring for keyboard/programmatic focus, but hides it
				 * for pointer focus.
				 */
				const isKeyboardFocus = !hadPointerDownRef.current && !isDragging;
				setIsFocusVisible(isKeyboardFocus);
				if (isKeyboardFocus && setIsKeyboardReorderToggleVisible) {
					setIsKeyboardReorderToggleVisible(true);
				}
				hadPointerDownRef.current = false;
			}}
			onBlur={(e) => {
				hadPointerDownRef.current = false;
				setIsFocusVisible(false);

				// Only exit reorder mode if focus is leaving the ribbon entirely
				const relatedTarget = e.relatedTarget as HTMLElement | null;
				const isStillInRibbon = containerRef.current?.contains(relatedTarget);
				if (!isStillInRibbon) {
					exitKeyboardReorderMode();
				}
			}}
		>
			{isDragging ? (
				<Stack alignInline="center" xcss={styles.appTile}>
					<UnsafeTile logo={icon} isFocused={isFocusVisible} isDisabled={isDragging} />
					<Text maxLines={1} size="small" align="center" color="color.text.disabled">
						{text}
					</Text>
				</Stack>
			) : (
				<Tooltip content={text}>
					{(tooltipProps) => (
						<Stack alignInline="center" xcss={styles.appTile} {...tooltipProps}>
							<UnsafeTile
								isEditMode={isKeyboardReorderModeEnabled}
								logo={icon}
								isSelected={
									(isSelected && !isKeyboardReorderModeEnabled) ||
									(isKeyboardReorderModeEnabled && selectedKeyboardReorderProductId === productId)
								}
								isFocused={isFocusVisible}
								hideNotch={hideNotch}
								isDragPreview={isDragPreview}
								isHovered={isHovered}
							/>

							{isDragPreview ? (
								<Box backgroundColor="elevation.surface" xcss={styles.labelContainer}>
									{textComponent}
								</Box>
							) : (
								textComponent
							)}
						</Stack>
					)}
				</Tooltip>
			)}
		</Anchor>
	);
};
